// [檔案： src/js/theme.js]

import * as state from './state.js';
import { dom } from './dom.js';
// ✅ 引入 Capacitor Preferences
import { Preferences } from '@capacitor/preferences';

/**
 * 根據 isDark 狀態應用主題
 * @param {boolean} isDark 
 */
export function applyTheme(isDark) {
    state.setCurrentThemeIsDark(isDark);
    
    if (isDark) {
        document.documentElement.classList.add('dark');
    } else {
        document.documentElement.classList.remove('dark');
    }

    // Toggle sun/moon icons in settings
    if (dom.themeIconSun && dom.themeIconMoon) {
        if (isDark) {
            dom.themeIconSun.classList.add('hidden');
            dom.themeIconMoon.classList.remove('hidden');
        } else {
            dom.themeIconSun.classList.remove('hidden');
            dom.themeIconMoon.classList.add('hidden');
        }
    }
    
    if (dom.themeToggleModal && dom.themeToggleHandle) {
        if (isDark) {
            dom.themeToggleModal.classList.remove('bg-slate-300', 'dark:bg-slate-700');
            dom.themeToggleModal.classList.add('bg-indigo-600');
            dom.themeToggleHandle.classList.add('translate-x-5');
            dom.themeToggleHandle.classList.remove('translate-x-0');
            dom.themeToggleModal.setAttribute('aria-checked', 'true');
        } else {
            dom.themeToggleModal.classList.add('bg-slate-300', 'dark:bg-slate-700');
            dom.themeToggleModal.classList.remove('bg-indigo-600');
            dom.themeToggleHandle.classList.remove('translate-x-5');
            dom.themeToggleHandle.classList.add('translate-x-0');
            dom.themeToggleModal.setAttribute('aria-checked', 'false');
        }
    }
    
    if (state.flatpickrInstance) {
        if (isDark) {
            state.flatpickrInstance.calendarContainer.classList.add('dark');
        } else {
            state.flatpickrInstance.calendarContainer.classList.remove('dark');
        }
    }
    
    if (state.monthSelectInstance) {
        state.monthSelectInstance.set('theme', isDark ? 'dark' : 'light');
        const container = state.monthSelectInstance.calendarContainer;
        if (isDark) {
            container.classList.add('flatpickr-monthSelect-theme-dark');
            container.classList.remove('flatpickr-monthSelect-theme-light');
        } else {
            container.classList.add('flatpickr-monthSelect-theme-light');
            container.classList.remove('flatpickr-monthSelect-theme-dark');
        }
    }
}

/**
 * 切換主題
 */
async function toggleTheme() { // ✅ 設為 async
    const isDark = !document.documentElement.classList.contains('dark');
    // ✅ 使用 Preferences.set
    await Preferences.set({
        key: 'theme',
        value: isDark ? 'dark' : 'light'
    });
    applyTheme(isDark);
}

/**
 * 根據 enabled 狀態增刪 running balance UI 樣式與狀態
 */
export function applyRunningBalance(enabled) {
    state.setShowRunningBalance(enabled);
    
    if (dom.runningBalanceToggle && dom.runningBalanceHandle) {
        if (enabled) {
            dom.runningBalanceToggle.classList.remove('bg-slate-300', 'dark:bg-slate-700');
            dom.runningBalanceToggle.classList.add('bg-indigo-600');
            dom.runningBalanceHandle.classList.add('translate-x-5');
            dom.runningBalanceHandle.classList.remove('translate-x-0');
            dom.runningBalanceToggle.setAttribute('aria-checked', 'true');
        } else {
            dom.runningBalanceToggle.classList.add('bg-slate-300', 'dark:bg-slate-700');
            dom.runningBalanceToggle.classList.remove('bg-indigo-600');
            dom.runningBalanceHandle.classList.remove('translate-x-5');
            dom.runningBalanceHandle.classList.add('translate-x-0');
            dom.runningBalanceToggle.setAttribute('aria-checked', 'false');
        }
    }
}

/**
 * 切換顯示每筆餘額
 */
async function toggleRunningBalance() {
    const nextVal = !state.showRunningBalance;
    await Preferences.set({
        key: 'showRunningBalance',
        value: nextVal ? 'true' : 'false'
    });
    applyRunningBalance(nextVal);
    // 觸發交易列表重繪
    window.dispatchEvent(new CustomEvent('app:transactionUpdated'));
}

/**
 * 初始化主題與偏好設定
 */
export async function initializeTheme() { // ✅ 設為 async
    // ✅ 使用 Preferences.get
    const { value: savedTheme } = await Preferences.get({ key: 'theme' });
    const { value: savedBalance } = await Preferences.get({ key: 'showRunningBalance' });
    
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    let isDark = (savedTheme === 'dark') || (!savedTheme && prefersDark);
    applyTheme(isDark);
    
    applyRunningBalance(savedBalance === 'true');

    if (dom.themeToggleModal) {
        dom.themeToggleModal.addEventListener('click', toggleTheme);
    }

    if (dom.runningBalanceToggle) {
        dom.runningBalanceToggle.addEventListener('click', toggleRunningBalance);
    }

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
        // ✅ 修正： 確保異步
        (async () => {
            const { value: savedTheme } = await Preferences.get({ key: 'theme' });
            if (!savedTheme) {
                applyTheme(e.matches);
            }
        })();
    });
}